import {
  DEAL_PAYROLL_CALCULATION_REVISION, DealPayrollBreakdown, DealPayrollGradeCode,
  DealPayrollIssue, DealPayrollTaskLine, isFinitePayrollNumber
} from '../../../models/deal-payroll.model';
import {
  dealPayrollChecklistKindKey, dealPayrollClassificationSourceKey, dealPayrollGradeKey,
  dealPayrollIssueActionKey, dealPayrollIssueEffectKey, dealPayrollIssueKey,
  dealPayrollTaskIssueEffectKey, dealPayrollTaskRuleKey
} from '../../../models/deal-payroll-presentation';
import {
  CurrencyAmount, CurrencyCode, PAYROLL_CURRENCY_CODES
} from '../../../models/payroll-currency.model';
import {PAYROLL_CALCULATION_INDEX, PAYROLL_MODIFIERS} from '../my-working-report.constants';
import {payrollNumberDisplay} from '../../../models/payroll-number-display';
import {
  CHECKLIST_UNIT_TYPE, DEFAULT_TASK_RULE, DEFAULT_TASK_RULE_ISSUE,
  EMPTY_COUNT, EMPTY_VALUE, FIRST_INDEX, GLOBAL_ISSUE_SCOPE,
  STANDARD_TASKS_GROUP_KEY, TASK_GROUP_KEY_SEPARATOR,
  UNCLASSIFIED_GROUP_KEY, UNCLASSIFIED_TASK_GROUP
} from './deal-payroll-detail.view.constants';
import {
  DealPayrollDetailIssueView, DealPayrollDetailTaskGroup,
  DealPayrollDetailTaskView, DealPayrollModifierView, TaskGroupCollection
} from './deal-payroll-detail.view.types';
import {hasTaskComponents, planAccruesInMonth, taskPlanReason} from './deal-payroll-detail.components';
export {
  DealPayrollDetailIssueView, DealPayrollDetailTaskGroup,
  DealPayrollDetailTaskView, DealPayrollModifierView
} from './deal-payroll-detail.view.types';

const INTERNAL_SHARE_KEY = 'PAYROLL_INTERNAL_SHARE';
const MONTHLY_BONUS_KEY = 'PAYROLL_MONTHLY_BONUS';
const FORMULA_MULTIPLY = ' × ';
const PERCENT_EQUALS = '% = ';
const VALUE_SPACE = ' ';
const TASK_GRADE_UNIT = 'TASK_GRADE';
const KNOWN_GRADE_CODE = /^(JUNIOR|MIDDLE|SENIOR)(_(MINUS|PLUS))?$/;

export function dealPayrollModifierViews(
  calculation: Array<string | number | null>, currency: CurrencyCode = null
): DealPayrollModifierView[] {
  const values = calculation || [];
  const modifiers: DealPayrollModifierView[] = PAYROLL_MODIFIERS.map(modifier => ({
    key: modifier.key,
    value: values[modifier.index] === undefined
      ? null : payrollNumberDisplay(values[modifier.index])
  }));
  return monthlyBonusViews(values, currency).concat(modifiers);
}

export function salaryCompositionViews(
  values: Array<string | number | null>, currency: CurrencyCode
): DealPayrollModifierView[] {
  const index = PAYROLL_CALCULATION_INDEX;
  const rows: DealPayrollModifierView[] = [];
  if (values[index.FULL_SALARY] != null && values[index.INTERNAL_SHARE] != null) {
    rows.push({key: INTERNAL_SHARE_KEY, value: payrollNumberDisplay(values[index.FULL_SALARY], true)
      + FORMULA_MULTIPLY + payrollNumberDisplay(values[index.INTERNAL_SHARE]) + PERCENT_EQUALS
      + payrollNumberDisplay(values[index.BASE_RATE], true) + VALUE_SPACE + (currency || EMPTY_VALUE)});
  }
  if (values[index.MONTHLY_BONUS] != null) {
    rows.push({key: MONTHLY_BONUS_KEY, value: payrollNumberDisplay(values[index.MONTHLY_BONUS], true)
      + VALUE_SPACE + (currency || EMPTY_VALUE)});
  }
  return rows;
}

export function monthlyBonusViews(values: Array<string | number | null>, currency: CurrencyCode): DealPayrollModifierView[] {
  return salaryCompositionViews(values, currency).filter(row => row.key === MONTHLY_BONUS_KEY);
}

export function dealPayrollFinalAmountViews(breakdown: DealPayrollBreakdown): CurrencyAmount[] {
  if (!breakdown) {
    return [];
  }
  const amounts = validCurrencyAmounts(breakdown.finalSalaryAmounts);
  const primary = primaryAmount(breakdown);
  const merged = primary ? replaceCurrencyAmount(amounts, primary) : amounts;
  return merged.sort(compareCurrencyAmounts);
}

export function dealPayrollVisibleIssueViews(
  breakdown: DealPayrollBreakdown
): DealPayrollDetailIssueView[] {
  const taskIssueKeys = dealPayrollTaskIssueKeys(breakdown);
  const seen: {[key: string]: boolean} = {};
  return (breakdown && breakdown.issues || [])
    .filter((issue: DealPayrollIssue) => visibleIssue(issue, taskIssueKeys))
    .filter((issue: DealPayrollIssue) => firstIssue(issue, seen))
    .map((issue: DealPayrollIssue) => dealPayrollIssueView(issue));
}

export function dealPayrollTaskGroups(
  breakdown: DealPayrollBreakdown
): DealPayrollDetailTaskGroup[] {
  const groups: DealPayrollDetailTaskGroup[] = [];
  const byKey: {[key: string]: DealPayrollDetailTaskGroup} = {};
  const collection = {groups: groups, byKey: byKey, month: breakdown && breakdown.month,
    calculationRevision: breakdown && breakdown.calculationRevision,
    globalIssues: globalIssueCodes(breakdown)};
  (breakdown && breakdown.taskLines || []).forEach((task: DealPayrollTaskLine) => {
    addTaskToGroup(collection, task);
  });
  return problemGroupsFirst(groups.map(finalizeGroup));
}

export function isDealPayrollTaskProblem(task: DealPayrollDetailTaskView): boolean {
  return !task || !isFinitePayrollNumber(task.result)
    || task.issueViews.length > EMPTY_COUNT;
}

function validCurrencyAmounts(amounts: CurrencyAmount[]): CurrencyAmount[] {
  return (amounts || [])
    .filter((amount: CurrencyAmount) => !!amount && validCurrency(amount.currency)
      && isFinitePayrollNumber(amount.amount))
    .map((amount: CurrencyAmount) => ({currency: amount.currency, amount: amount.amount}));
}

function primaryAmount(breakdown: DealPayrollBreakdown): CurrencyAmount {
  return validCurrency(breakdown.salaryCurrency) && isFinitePayrollNumber(breakdown.finalSalary)
    ? {currency: breakdown.salaryCurrency, amount: breakdown.finalSalary}
    : undefined;
}

function validCurrency(currency: CurrencyCode): boolean {
  return PAYROLL_CURRENCY_CODES.indexOf(currency) >= FIRST_INDEX;
}

function replaceCurrencyAmount(amounts: CurrencyAmount[], primary: CurrencyAmount): CurrencyAmount[] {
  return amounts.filter((amount: CurrencyAmount) => amount.currency !== primary.currency)
    .concat([primary]);
}

function compareCurrencyAmounts(left: CurrencyAmount, right: CurrencyAmount): number {
  return PAYROLL_CURRENCY_CODES.indexOf(left.currency) - PAYROLL_CURRENCY_CODES.indexOf(right.currency);
}

function visibleIssue(issue: DealPayrollIssue, taskIssueKeys: {[key: string]: boolean}): boolean {
  const defaultRuleVisible = issue.code !== DEFAULT_TASK_RULE_ISSUE || issue.blocking;
  return defaultRuleVisible && !taskIssueKeys[taskIssueKey(issue.taskId, issue.code)];
}

function firstIssue(issue: DealPayrollIssue, seen: {[key: string]: boolean}): boolean {
  const key = taskIssueKey(issue.taskId, issue.code);
  if (seen[key]) {
    return false;
  }
  seen[key] = true;
  return true;
}

function dealPayrollIssueView(issue: DealPayrollIssue): DealPayrollDetailIssueView {
  return Object.assign({}, issue, {
    causeKey: dealPayrollIssueKey(issue.code),
    effectKey: dealPayrollIssueEffectKey(issue),
    actionKey: dealPayrollIssueActionKey(issue.code)
  });
}

function dealPayrollTaskIssueKeys(breakdown: DealPayrollBreakdown): {[key: string]: boolean} {
  const keys: {[key: string]: boolean} = {};
  (breakdown && breakdown.taskLines || []).forEach((task: DealPayrollTaskLine) => {
    taskIssueCodes(task).forEach((code: string) => keys[taskIssueKey(task.taskId, code)] = true);
  });
  return keys;
}

function taskIssueKey(taskId: number | null, code: string): string {
  const scope = taskId === null || taskId === undefined ? GLOBAL_ISSUE_SCOPE : String(taskId);
  return scope + TASK_GROUP_KEY_SEPARATOR + code;
}

function addTaskToGroup(
  collection: TaskGroupCollection,
  task: DealPayrollTaskLine
): void {
  const key = taskGroupKey(task);
  const group = collection.byKey[key] || createGroup(key, task);
  if (!collection.byKey[key]) {
    collection.byKey[key] = group;
    collection.groups.push(group);
  }
  addTask(group, task, collection);
}

function createGroup(key: string, task: DealPayrollTaskLine): DealPayrollDetailTaskGroup {
  const defaultRuleIssue = hasDefaultRuleIssue(task);
  return {
    key: key,
    displayName: taskGroupName(task),
    displayNameKey: taskGroupNameKey(task),
    ruleLabelKey: dealPayrollTaskRuleKey(task.taskRuleCode),
    defaultRuleIssue: defaultRuleIssue,
    rows: [],
    coefficient: numericValue(task.areaCoefficient),
    coefficientMismatch: false,
    checklistRuleHours: task.checklistRuleHours,
    issueCount: EMPTY_COUNT
  };
}

function addTask(
  group: DealPayrollDetailTaskGroup,
  task: DealPayrollTaskLine,
  context: TaskGroupCollection
): void {
  const row = taskView(task, context);
  group.rows.push(row);
  updateDefaultRuleIssue(group, task);
  updateGroupCoefficient(group, task.areaCoefficient);
  group.issueCount += row.issueViews.filter(
    (issue: DealPayrollDetailIssueView) => !context.globalIssues[issue.code]).length;
}

function updateDefaultRuleIssue(
  group: DealPayrollDetailTaskGroup,
  task: DealPayrollTaskLine
): void {
  if (!group.defaultRuleIssue && hasDefaultRuleIssue(task)) {
    group.defaultRuleIssue = true;
  }
}

function hasDefaultRuleIssue(task: DealPayrollTaskLine): boolean {
  return !!task && task.taskRuleCode === DEFAULT_TASK_RULE
    && (task.issues || []).indexOf(DEFAULT_TASK_RULE_ISSUE) >= FIRST_INDEX;
}

function taskView(task: DealPayrollTaskLine, context: TaskGroupCollection): DealPayrollDetailTaskView {
  const current = context.calculationRevision === DEAL_PAYROLL_CALCULATION_REVISION;
  const value = current ? task : Object.assign({}, task,
    {completed: task.completed === true ? true : null, plannedAmount: null, actualAmount: null});
  const planPaid = planAccruesInMonth(value, context.month);
  return Object.assign({}, value, {
    planPaidInMonth: planPaid,
    plannedPaymentHours: planPaid === null ? null : (planPaid ? task.plannedHours : EMPTY_COUNT),
    componentsAvailable: hasTaskComponents(value),
    planReasonKey: taskPlanReason(value, context.month),
    ruleLabelKey: dealPayrollTaskRuleKey(task.taskRuleCode),
    classificationSourceLabelKey: dealPayrollClassificationSourceKey(task.classificationSource),
    issueViews: taskIssueViews(task)
  }) as DealPayrollDetailTaskView;
}

function taskIssueViews(task: DealPayrollTaskLine): DealPayrollDetailIssueView[] {
  return taskIssueCodes(task).map((code: string) => ({
      code: code,
      taskId: task.taskId,
      blocking: !isFinitePayrollNumber(task.result),
      causeKey: dealPayrollIssueKey(code),
      effectKey: dealPayrollTaskIssueEffectKey(task, code),
      actionKey: dealPayrollIssueActionKey(code)
    }));
}

function taskIssueCodes(task: DealPayrollTaskLine): string[] {
  return (task && task.issues || [])
    .filter((code: string, index: number, codes: string[]) => codes.indexOf(code) === index);
}

function updateGroupCoefficient(group: DealPayrollDetailTaskGroup, value: number): void {
  const coefficient = numericValue(value);
  if (group.coefficient === null && !group.coefficientMismatch && coefficient !== null) {
    group.coefficient = coefficient;
    return;
  }
  if (group.coefficient !== null && coefficient !== null && group.coefficient !== coefficient) {
    group.coefficient = null;
    group.coefficientMismatch = true;
    group.issueCount++;
  }
}

function finalizeGroup(group: DealPayrollDetailTaskGroup): DealPayrollDetailTaskGroup {
  const problemRows = group.rows.filter(isDealPayrollTaskProblem);
  const validRows = group.rows.filter((row: DealPayrollDetailTaskView) => !isDealPayrollTaskProblem(row));
  group.rows = problemRows.concat(validRows);
  return group;
}

function problemGroupsFirst(groups: DealPayrollDetailTaskGroup[]): DealPayrollDetailTaskGroup[] {
  const problem = groups.filter(isGroupProblem);
  return problem.concat(groups.filter((group: DealPayrollDetailTaskGroup) => !isGroupProblem(group)));
}

function isGroupProblem(group: DealPayrollDetailTaskGroup): boolean {
  return group.issueCount > EMPTY_COUNT || group.rows.some(isDealPayrollTaskProblem);
}

function taskGroupKey(task: DealPayrollTaskLine): string {
  const rule = task && task.taskRuleCode || UNCLASSIFIED_TASK_GROUP;
  if (!task || !task.coefficientUnitType || !task.coefficientUnitCode) {
    return rule + TASK_GROUP_KEY_SEPARATOR + UNCLASSIFIED_TASK_GROUP;
  }
  const checklist = task.coefficientUnitType === CHECKLIST_UNIT_TYPE && task.checklistKind
    ? TASK_GROUP_KEY_SEPARATOR + task.checklistKind : EMPTY_VALUE;
  return rule + TASK_GROUP_KEY_SEPARATOR + task.coefficientUnitType
    + TASK_GROUP_KEY_SEPARATOR + task.coefficientUnitCode + checklist;
}

function taskGroupName(task: DealPayrollTaskLine): string {
  if ((task && task.taskRuleCode === DEFAULT_TASK_RULE) || taskGradeNameKey(task)) {
    return EMPTY_VALUE;
  }
  if (task && task.coefficientUnitType === CHECKLIST_UNIT_TYPE && task.checklistKind) {
    return EMPTY_VALUE;
  }
  return task && task.coefficientUnitName || EMPTY_VALUE;
}

function taskGroupNameKey(task: DealPayrollTaskLine): string {
  if (task && task.taskRuleCode === DEFAULT_TASK_RULE) {
    return STANDARD_TASKS_GROUP_KEY;
  }
  if (task && task.coefficientUnitType === CHECKLIST_UNIT_TYPE && task.checklistKind) {
    return dealPayrollChecklistKindKey(task.checklistKind);
  }
  if (taskGradeNameKey(task)) {
    return taskGradeNameKey(task);
  }
  return taskGroupName(task) ? EMPTY_VALUE : UNCLASSIFIED_GROUP_KEY;
}

function taskGradeNameKey(task: DealPayrollTaskLine): string {
  return task && task.coefficientUnitType === TASK_GRADE_UNIT && KNOWN_GRADE_CODE.test(task.coefficientUnitCode)
    ? dealPayrollGradeKey(task.coefficientUnitCode as DealPayrollGradeCode) : EMPTY_VALUE;
}

function globalIssueCodes(breakdown: DealPayrollBreakdown): {[code: string]: boolean} {
  const result: {[code: string]: boolean} = {};
  (breakdown && breakdown.issues || [])
    .filter((issue: DealPayrollIssue) => issue.blocking && !issue.taskId)
    .forEach((issue: DealPayrollIssue) => result[issue.code] = true);
  return result;
}

function numericValue(value: number): number | null {
  return isFinitePayrollNumber(value) ? Number(value) : null;
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/my-working-report/deal-payroll-detail/deal-payroll-detail.view.ts